// COUPLING: everything here patches the SVG that Apex draws, through its
// internal class names (`g.apexcharts-bar-series`, `path.apexcharts-bar-area`,
// `.apexcharts-xaxis-label`, `.apexcharts-yaxis-annotations`,
// `.apexcharts-inner`) and the `el` on its chart instance. None of that is
// public API. Verified against apexcharts 7.8.0 (node_modules); re-check these
// selectors when upgrading it.
//
// Bars have 16px top and 4px bottom corners, lifted 8px off the x-axis line —
// the expanded card's design. A narrow bar gets a proportionally smaller top
// radius, else 16px reads as a half circle.
export const TOP_RADIUS_PX = 16
const TOP_RADIUS_SHARE = 0.25
const BOTTOM_RADIUS_PX = 4
const AXIS_GAP_PX = 8

/** The part of Apex's chart instance used here (its types don't cover `el`). */
export interface ApexChart {
  el: HTMLElement
  w?: {
    globals?: {
      maxYArr?: number[]
      minYArr?: number[]
      gridHeight?: number
    }
  }
}

const isApexChart = (chart: unknown): chart is ApexChart =>
  typeof chart === 'object' &&
  chart !== null &&
  'el' in chart &&
  chart.el instanceof HTMLElement

const chartElement = (chart: unknown): HTMLElement | undefined =>
  isApexChart(chart) ? chart.el : undefined

/** The data point an Apex event's options name, if they name one. */
export const dataPointIndexOf = (options: unknown): number | null =>
  typeof options === 'object' &&
  options !== null &&
  'dataPointIndex' in options &&
  typeof options.dataPointIndex === 'number'
    ? options.dataPointIndex
    : null

/** The data point a click names; its options carry it as a string. */
export const clickedIndexOf = (options: unknown): number | null => {
  if (typeof options !== 'object' || options === null) return null
  if (!('dataPointIndex' in options)) return null
  const raw = options.dataPointIndex
  const index = typeof raw === 'string' && raw !== '' ? Number(raw) : raw
  return typeof index === 'number' && Number.isInteger(index) && index >= 0
    ? index
    : null
}

// After a data update or a resize Apex hides the markers of a line until an
// animation completes, which never comes with dynamic animations off: the
// markers of a short line stay invisible. Show them. A line starting with an
// empty point also gets a ~0px marker at the plot's corner, which its stroke
// turns into a stray dot: hide those.
export const showMarkers = (chart: unknown) => {
  const root = chartElement(chart)
  root
    ?.querySelectorAll('.apexcharts-series-markers-wrap')
    .forEach(wrap => wrap.classList.remove('apexcharts-element-hidden'))
  root
    ?.querySelectorAll<SVGPathElement>('path.apexcharts-marker')
    .forEach(marker => {
      if (marker.getBBox().width < 1) marker.style.display = 'none'
    })
}

// Apex has no option for a gap between the bars and the axis line; moving the
// series group moves each bar together with its hover target.
export const liftBarsOffAxis = (chart: unknown) => {
  const series = chartElement(chart)?.querySelector<SVGGElement>(
    'g.apexcharts-bar-series'
  )
  if (series) series.style.transform = `translateY(-${AXIS_GAP_PX}px)`
}

// Pixels per data unit on the value axis, from the chart's own rendered scale.
const pixelsPerUnit = (chart: unknown): number | null => {
  const globals = isApexChart(chart) ? chart.w?.globals : undefined
  const span = (globals?.maxYArr?.[0] ?? 0) - (globals?.minYArr?.[0] ?? 0)
  const height = globals?.gridHeight ?? 0
  return span > 0 && height > 0 ? height / span : null
}

// Apex draws a column down to 0, so on an axis starting above it a bar
// overshoots the grid. Clip it to the part above the axis' minimum, growing it
// by the same share as Apex's entrance (native height over final height).
const clipBar = (
  y: number,
  rawH: number,
  yMin: number,
  value: number | undefined,
  perUnit: number | null
) => {
  if (!(yMin > 0 && value !== undefined && value > 0)) return { y, h: rawH }
  if (!perUnit) return { y, h: (rawH * (value - yMin)) / value }
  const progress = Math.min(1, rawH / (value * perUnit))
  const h = progress * (value - yMin) * perUnit
  return { y: y + rawH - yMin * perUnit - h, h }
}

/** A bar's outline: round on top, a small radius below. */
const roundedBarPath = (
  x: number,
  y: number,
  w: number,
  h: number,
  top: number,
  bottom: number
) =>
  `M${x + top} ${y}H${x + w - top}A${top} ${top} 0 0 1 ${x + w} ${y + top}` +
  `V${y + h - bottom}A${bottom} ${bottom} 0 0 1 ${x + w - bottom} ${y + h}` +
  `H${x + bottom}A${bottom} ${bottom} 0 0 1 ${x} ${y + h - bottom}` +
  `V${y + top}A${top} ${top} 0 0 1 ${x + top} ${y}Z`

const barRadii = (w: number, h: number) => ({
  top: Math.min(TOP_RADIUS_PX, w * TOP_RADIUS_SHARE, h / 2),
  bottom: Math.min(BOTTOM_RADIUS_PX, w / 2, h / 2),
})

// Apex rounds both ends of a bar alike, or only the top. Once drawn, each bar
// gets the design's 16px-top / 4px-bottom shape, and a clip of the same shape
// so a stroke (a projected bar's dashes) only shows inside the bar.
let clipSequence = 0
export const shapeBars = (
  chart: unknown,
  yMin = 0,
  values: (number | undefined)[] = []
) => {
  const svg = chartElement(chart)?.querySelector('svg')
  if (!svg) return
  const ns = 'http://www.w3.org/2000/svg'
  const defs =
    svg.querySelector('defs') ??
    svg.insertBefore(document.createElementNS(ns, 'defs'), svg.firstChild)
  const perUnit = pixelsPerUnit(chart)
  svg
    .querySelectorAll<SVGPathElement>('path.apexcharts-bar-area')
    .forEach((bar, i) => {
      const { x, y: rawY, width: w, height: rawH } = bar.getBBox()
      // rawH matches our own last-written height whenever Apex hasn't repainted
      // the bar since (we only ever shrink it): reclipping that would clip twice.
      if (Math.abs(rawH - Number(bar.dataset['lastH'] ?? NaN)) < 0.05) return
      const { y, h } = clipBar(rawY, rawH, yMin, values[i], perUnit)
      if (!(w > 0 && h > 0)) return
      const { top, bottom } = barRadii(w, h)
      const d = roundedBarPath(x, y, w, h, top, bottom)
      bar.setAttribute('d', d)
      bar.dataset['lastH'] = String(h)
      const id = bar.dataset['clipId'] ?? `ln-bar-clip-${clipSequence++}`
      bar.dataset['clipId'] = id
      let clip = defs.querySelector(`#${id}`)
      if (!clip) {
        clip = document.createElementNS(ns, 'clipPath')
        clip.id = id
        clip.appendChild(document.createElementNS(ns, 'path'))
        defs.appendChild(clip)
      }
      clip.firstElementChild?.setAttribute('d', d)
      bar.setAttribute('clip-path', `url(#${id})`)
    })
}

const RING_PX = 4
const RING_CLASS = 'ln-bar-ring'
const GHOST_CLASS = 'ln-ghost'

const removeAll = (chart: unknown, className: string) =>
  chartElement(chart)
    ?.querySelectorAll(`.${className}`)
    .forEach(node => node.remove())

const insertPathBefore = (
  bar: SVGPathElement,
  className: string,
  d: string
) => {
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('class', className)
  path.setAttribute('d', d)
  bar.parentNode?.insertBefore(path, bar)
}

export const hideBarRing = (chart: unknown) => removeAll(chart, RING_CLASS)

// Apex hides its tooltip on mouseleave, which never comes when the pointer
// leaves with the browser (switching tabs or windows): the tooltip stays up
// beside the next one. Hides the tooltip, its crosshairs and the bar ring.
export const hideHover = (root: HTMLElement | null) => {
  if (!root) return
  root
    .querySelectorAll('.apexcharts-tooltip, .apexcharts-xcrosshairs')
    .forEach(node => node.classList.remove('apexcharts-active'))
  root.querySelectorAll(`.${RING_CLASS}`).forEach(node => node.remove())
}

// The hover ring sits outside the bar, which is clipped to its own shape, so it
// is a larger shape drawn behind it. The selected bar has none.
export const showBarRing = (chart: unknown, index: number) => {
  hideBarRing(chart)
  const bar = chartElement(chart)?.querySelectorAll<SVGPathElement>(
    'path.apexcharts-bar-area'
  )[index]
  if (!bar?.parentNode || bar.dataset['kind'] === 'selected') return
  const { x, y, width: w, height: h } = bar.getBBox()
  if (!(w > 0 && h > 0)) return
  const { top, bottom } = barRadii(w, h)
  insertPathBefore(
    bar,
    RING_CLASS,
    roundedBarPath(
      x - RING_PX,
      y - RING_PX,
      w + 2 * RING_PX,
      h + 2 * RING_PX,
      top + RING_PX,
      bottom + RING_PX
    )
  )
}

// The outlook of a year under way: a pale bar behind the year's own, from the
// axis up to the projection (`ghost - yMin` units tall).
export const drawGhosts = (
  chart: unknown,
  yMin: number,
  ghosts: (number | null)[]
) => {
  removeAll(chart, GHOST_CLASS)
  const perUnit = pixelsPerUnit(chart)
  if (!perUnit) return
  chartElement(chart)
    ?.querySelectorAll<SVGPathElement>('path.apexcharts-bar-area')
    .forEach(bar => {
      const ghost = ghosts[Number(bar.getAttribute('j'))]
      if (ghost === null || ghost === undefined) return
      const { x, y, width: w, height: h } = bar.getBBox()
      const height = (ghost - yMin) * perUnit
      if (!(w > 0 && h > 0 && height > h)) return
      const { top, bottom } = barRadii(w, height)
      insertPathBefore(
        bar,
        GHOST_CLASS,
        roundedBarPath(x, y + h - height, w, height, top, bottom)
      )
    })
}

// Marks bars for the CSS: the selected one is green, a projected one (the
// outlook for a month not yet reported) pale and dashed. Apex numbers a bar's
// data point in its `j` attribute; it redraws the bars on an update, so this
// runs after each draw.
export const markBars = (chart: unknown, kinds: (string | undefined)[]) =>
  chartElement(chart)
    ?.querySelectorAll<SVGPathElement>('path.apexcharts-bar-area')
    .forEach(bar => {
      const kind = kinds[Number(bar.getAttribute('j'))]
      if (kind) bar.dataset['kind'] = kind
      else delete bar.dataset['kind']
    })

// The hovered and selected bars' x labels are medium weight in the primary
// text colour. Matched by text, not index: Apex may drop labels.
export const emphasiseLabels = (
  chart: unknown,
  ...emphasised: (string | null | undefined)[]
) =>
  chartElement(chart)
    ?.querySelectorAll<SVGTextElement>('.apexcharts-xaxis-label')
    .forEach(text => {
      const label = text.textContent?.trim() ?? ''
      const on = emphasised.some(
        entry =>
          entry !== null && entry !== undefined && label.startsWith(entry)
      )
      text.style.fontWeight = on ? '500' : ''
      text.style.fill = on ? 'var(--ln-text)' : ''
    })
